import React, { useState, useEffect } from 'react';
import { 
    Container, 
    Box, 
    Typography, 
    Paper, 
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Alert,
    CircularProgress,
    Tabs,
    Tab,
    Divider,
    TextField
} from '@mui/material';
import ProfileDetails from '../components/profile/ProfileDetails';
import ChangeProfilePhoto from '../components/profile/ChangeProfilePhoto';
import SkillsSection from '../components/profile/SkillsSection';
import EducationSection from '../components/profile/EducationSection';
import CertificationSection from '../components/profile/CertificationSection';
import LanguageSection from '../components/profile/LanguageSection';
import useProfile from '../hooks/UseProfile';
import ProfileEditScreen from '../components/ProfileEditScreen';
import MainLayout from 'core/layout/MainLayout';
import { HttpService } from 'core/services/http';
import { UserHttpService } from 'core/services/user';
import { Utils } from 'core/utils';
import { useAuth0 } from "@auth0/auth0-react";
import CustomModal from 'cms/core/shared/dialogs/CustomModal';
import LoginBox from '../components/LoginBox';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PersonIcon from '@mui/icons-material/Person';
import SettingsIcon from '@mui/icons-material/Settings';

const ProfilePage = () => {
    const { data: profile, isLoading, error, updateProfile, refetch } = useProfile();
    const [isEditing, setIsEditing] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [loginModalIsOpen, setLoginModalIsOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState('');
    const [deletePassword, setDeletePassword] = useState('');
    const [activeTab, setActiveTab] = useState(0);
    const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
    const { isAuthenticated: isAuth0Authenticated, logout: auth0Logout } = useAuth0();

    useEffect(() => {
        // Check authentication status
        const checkAuth = () => {
            const token = HttpService.getToken();
            const user = HttpService.getUser();
            const authenticated = !!token && !!user;
            setIsAuthenticated(authenticated);
            setCheckingAuth(false);
        };

        // Check immediately
        checkAuth();

        // Listen for storage events (e.g., after login in another tab or after modal closes)
        window.addEventListener('storage', checkAuth);

        // Also check periodically (in case of same-tab login)
        const interval = setInterval(checkAuth, 1000);

        return () => {
            window.removeEventListener('storage', checkAuth);
            clearInterval(interval);
        };
    }, []);

    const handleProfileUpdate = async (data) => {
        try {
            await updateProfile(data);
            setIsEditing(false);
        } catch (err) {
            // Error handling is done within the hook
        }
    };

    const handleDeleteAccount = async () => {
        // Validate password is provided
        if (!deletePassword || deletePassword.trim() === '') {
            setDeleteError('Please enter your password to confirm account deletion.');
            return;
        }
        
        setDeleting(true);
        setDeleteError('');
        
        try {
            const response = await UserHttpService.deleteProfile(deletePassword);
            
            if (response?.error) {
                setDeleteError(response.error);
                setDeleting(false);
                return;
            }
            
            // Successfully deleted - logout and redirect
            setDeleteDialogOpen(false);
            setDeletePassword('');
            
            // Clear session
            Utils.removeUserSession();
            
            // Handle Auth0 logout if applicable
            if (isAuth0Authenticated) {
                auth0Logout({ logoutParams: { returnTo: window.location.origin } });
            } else {
                // Redirect to home page
                window.location.href = '/';
            }
        } catch (err) {
            console.error('Error deleting account:', err);
            const errorMessage = err?.response?.data?.error || err?.message || 'Failed to delete account. Please try again.';
            setDeleteError(errorMessage);
            setDeleting(false);
        }
    };

    const handleDeleteDialogClose = () => {
        if (!deleting) {
            setDeleteDialogOpen(false);
            setDeleteError('');
            setDeletePassword('');
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    if (!isAuthenticated && !checkingAuth) {
        return (
            <MainLayout>
                <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
                    <Paper elevation={3} sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
                        <Typography variant="h4" gutterBottom fontWeight={600}>
                            Please Sign In
                        </Typography>
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, mt: 2 }}>
                            You need to be logged in to view and manage your profile.
                        </Typography>
                        <Button
                            variant="contained"
                            size="large"
                            onClick={() => setLoginModalIsOpen(true)}
                            sx={{ px: 4 }}
                        >
                            Sign In
                        </Button>
                        <CustomModal 
                            big 
                            key={loginModalIsOpen} 
                            setOpen={(value) => setLoginModalIsOpen(value)}
                            open={loginModalIsOpen} 
                            showClose={true}
                        >
                            <LoginBox onClose={() => setLoginModalIsOpen(false)} />
                        </CustomModal>
                    </Paper>
                </Container>
            </MainLayout>
        );
    }

    return (
        <MainLayout>
            <Container maxWidth="lg" sx={{ py: 4 }}>
                {isEditing ? (
                    <ProfileEditScreen
                        profile={profile}
                        onSubmit={handleProfileUpdate}
                        onCancel={() => setIsEditing(false)}
                    />
                ) : (
                    <Box>
                        {/* Header */}
                        <Box sx={{ mb: 4 }}>
                            <Typography variant="h4" fontWeight={700} gutterBottom>
                                My Profile
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Manage your profile information and account settings
                            </Typography>
                        </Box>

                        {/* Tabs */}
                        <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, mb: 3 }}>
                            <Tabs 
                                value={activeTab} 
                                onChange={handleTabChange}
                                sx={{ 
                                    borderBottom: 1, 
                                    borderColor: 'divider',
                                    px: 2
                                }}
                            >
                                <Tab 
                                    icon={<PersonIcon />} 
                                    iconPosition="start"
                                    label="Public Profile" 
                                    sx={{ minHeight: 64 }}
                                />
                                <Tab 
                                    icon={<SettingsIcon />} 
                                    iconPosition="start"
                                    label="Account Settings" 
                                    sx={{ minHeight: 64 }}
                                />
                            </Tabs>
                        </Paper>

                        {/* Tab Content */}
                        <Box>
                            {activeTab === 0 && (
                                <Box>

                                    {/* Profile Details */}
                                    <Box sx={{ mb: 3 }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                            <Typography variant="h6" fontWeight={600}>
                                                Profile Information
                                            </Typography>
                                            <Button
                                                variant="outlined"
                                                size="small"
                                                onClick={() => setIsEditing(true)}
                                            >
                                                Edit Profile
                                            </Button>
                                        </Box>
                                        <ProfileDetails 
                                            profile={profile} 
                                            onAvatarClick={() => setPhotoDialogOpen(true)}
                                            onNameClick={() => setIsEditing(true)}
                                        />
                                    </Box>

                                    {/* Skills Section */}
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 3, 
                                            mb: 3, 
                                            border: '1px solid', 
                                            borderColor: 'divider',
                                            borderRadius: 2
                                        }}
                                    >
                                        <SkillsSection />
                                    </Paper>

                                    {/* Education Section */}
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 3, 
                                            mb: 3, 
                                            border: '1px solid', 
                                            borderColor: 'divider',
                                            borderRadius: 2
                                        }}
                                    >
                                        <EducationSection />
                                    </Paper>

                                    {/* Certification Section */}
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 3, 
                                            mb: 3, 
                                            border: '1px solid', 
                                            borderColor: 'divider',
                                            borderRadius: 2
                                        }}
                                    >
                                        <CertificationSection />
                                    </Paper>

                                    {/* Language Section */}
                                    <Paper 
                                        elevation={0} 
                                        sx={{ 
                                            p: 3, 
                                            mb: 3, 
                                            border: '1px solid', 
                                            borderColor: 'divider',
                                            borderRadius: 2
                                        }}
                                    >
                                        <LanguageSection />
                                    </Paper>
                                </Box>
                            )}

                            {activeTab === 1 && (
                                <Paper 
                                    elevation={0} 
                                    sx={{ 
                                        p: 4, 
                                        border: '1px solid', 
                                        borderColor: 'divider',
                                        borderRadius: 2
                                    }}
                                >
                                    <Typography variant="h6" fontWeight={600} gutterBottom>
                                        Account Settings
                                    </Typography>
                                    <Divider sx={{ my: 3 }} />
                                    
                                    {/* Delete Account Section */}
                                    <Box 
                                        sx={{ 
                                            p: 3, 
                                            border: '2px solid', 
                                            borderColor: 'error.main',
                                            borderRadius: 2,
                                            bgcolor: 'error.light',
                                            bgcolor: 'rgba(211, 47, 47, 0.04)'
                                        }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                                            <DeleteOutlineIcon sx={{ color: 'error.main', mr: 2, mt: 0.5 }} />
                                            <Box sx={{ flex: 1 }}>
                                                <Typography variant="h6" color="error" fontWeight={600} gutterBottom>
                                                    Delete Account
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                                    Once you delete your account, there is no going back. This action cannot be undone.
                                                    All your data including profile, applications, CVs, and other information will be permanently deleted.
                                                </Typography>
                                                <Button
                                                    variant="outlined"
                                                    color="error"
                                                    startIcon={<DeleteOutlineIcon />}
                                                    onClick={() => setDeleteDialogOpen(true)}
                                                    sx={{ 
                                                        borderWidth: 2,
                                                        '&:hover': {
                                                            borderWidth: 2
                                                        }
                                                    }}
                                                >
                                                    Delete My Account
                                                </Button>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Delete Account Confirmation Dialog */}
                <Dialog
                    open={deleteDialogOpen}
                    onClose={handleDeleteDialogClose}
                    maxWidth="sm"
                    fullWidth
                >
                    <DialogTitle sx={{ pb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DeleteOutlineIcon color="error" />
                            <Typography variant="h6" fontWeight={600}>
                                Delete Account
                            </Typography>
                        </Box>
                    </DialogTitle>
                    <DialogContent>
                        {deleteError && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {deleteError}
                            </Alert>
                        )}
                        <DialogContentText sx={{ mb: 3 }}>
                            Are you absolutely sure you want to delete your account? This action cannot be undone.
                        </DialogContentText>
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(211, 47, 47, 0.04)', borderRadius: 1, mb: 3 }}>
                            <Typography variant="body2" fontWeight={600} color="error" gutterBottom>
                                What will be deleted:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" component="ul" sx={{ m: 0, pl: 2 }}>
                                <li>Your profile information</li>
                                <li>All job applications</li>
                                <li>Your CVs and documents</li>
                                <li>Bookmarked jobs</li>
                                <li>All other account data</li>
                            </Typography>
                        </Box>
                        <TextField
                            fullWidth
                            type="password"
                            label="Enter your password to confirm"
                            value={deletePassword}
                            onChange={(e) => {
                                setDeletePassword(e.target.value);
                                setDeleteError(''); // Clear error when user types
                            }}
                            disabled={deleting}
                            error={!!deleteError && deleteError.includes('password')}
                            helperText={deleteError && deleteError.includes('password') ? deleteError : 'You must enter your password to delete your account'}
                            sx={{ mt: 1 }}
                            autoFocus
                            onKeyPress={(e) => {
                                if (e.key === 'Enter' && deletePassword && !deleting) {
                                    handleDeleteAccount();
                                }
                            }}
                        />
                    </DialogContent>
                    <DialogActions sx={{ p: 2, pt: 1 }}>
                        <Button
                            onClick={handleDeleteDialogClose}
                            disabled={deleting}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleDeleteAccount}
                            color="error"
                            variant="contained"
                            disabled={deleting || !deletePassword || deletePassword.trim() === ''}
                            startIcon={deleting ? <CircularProgress size={20} /> : <DeleteOutlineIcon />}
                        >
                            {deleting ? 'Deleting...' : 'Delete Account'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Change Profile Photo Dialog */}
                <ChangeProfilePhoto
                    profile={profile}
                    onUpdate={() => refetch()}
                    open={photoDialogOpen}
                    onClose={() => setPhotoDialogOpen(false)}
                />
            </Container>
        </MainLayout>
    );
};

export default ProfilePage;
